import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface QueryResultProps {
  children: React.ReactNode;
  isError: boolean;
  error: Error | null;
  data: unknown;
  entityName: string;
  backTo: string;
  backLabel: string;
}

export function QueryResult({
  children,
  isError,
  error,
  data,
  entityName,
  backTo,
  backLabel,
}: QueryResultProps) {
  const navigate = useNavigate();

  if (isError && !data) {
    const isOffline = !navigator.onLine;
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="ghost" onClick={() => navigate(backTo)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <h3 className="text-lg font-medium text-foreground mb-1">
              {isOffline ? "Connection error" : `Failed to load ${entityName}`}
            </h3>
            <p className="text-muted-foreground">
              {isOffline
                ? "You appear to be offline. Please check your connection."
                : error?.message || "An unexpected error occurred."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    const label = entityName.charAt(0).toUpperCase() + entityName.slice(1);
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="ghost" onClick={() => navigate(backTo)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>
        <Card>
          <CardContent className="p-12 text-center">
            <h3 className="text-lg font-medium text-foreground mb-1">
              {label} not found
            </h3>
            <p className="text-muted-foreground">
              The {entityName} you're looking for doesn't exist or has been
              deleted.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
